// The renewals calendar's page turn, shared by the dashboard strip
// (renewals_strip_controller) and the Review modal's Desk
// (renewals_desk_controller), so the two always move the same way.
//
// lift (anticipation) → turn over the top binding (arc, ease-in-out, the page
// darkening as it faces away) → `swap()` paints the new date → the page drops
// back in with a small rebound while the pad squashes 6% (squash and stretch).
//
// The next page is already there underneath: `paint(el)` writes the next date
// onto a copy of the page laid behind it, so the turning page uncovers the
// date that follows, as a real tear-off pad does. The copy goes once the page
// has landed on top of it, showing the same date.
export const EASE_OUT = "cubic-bezier(0.23, 1, 0.32, 1)"
export const EASE_IN_OUT = "cubic-bezier(0.77, 0, 0.175, 1)"

export async function turnCalendarPage({ page, shade, pad, swap, paint }) {
  const under = paint ? pageUnderneath(page, paint) : null
  await page.animate(
    [{ transform: "rotateX(0)" }, { transform: "rotateX(10deg) translateY(-1px)" }],
    { duration: 140, easing: EASE_OUT, fill: "forwards" }
  ).finished
  shade.animate([{ opacity: 0 }, { opacity: 0.35 }], { duration: 260, easing: EASE_IN_OUT, fill: "forwards" })
  await page.animate(
    [{ transform: "rotateX(10deg) translateY(-1px)" }, { transform: "rotateX(-92deg) translateY(-1px)" }],
    { duration: 260, easing: EASE_IN_OUT, fill: "forwards" }
  ).finished

  swap()
  shade.getAnimations().forEach((a) => a.cancel())

  pad.animate(
    [{ transform: "none" }, { transform: "scale(1.06, 0.94)", offset: 0.55 }, { transform: "scale(0.98, 1.02)", offset: 0.8 }, { transform: "none" }],
    { duration: 340, delay: 120, easing: EASE_OUT }
  )
  await page.animate(
    [{ transform: "rotateX(78deg)" }, { transform: "rotateX(-6deg)", offset: 0.62 }, { transform: "rotateX(0)" }],
    { duration: 300, easing: EASE_OUT, fill: "forwards" }
  ).finished
  under?.remove()
}

// A copy of the page, laid just behind it, with the next date painted on.
// Stimulus target attributes come off, so the controller keeps writing to the
// real page, not the copy.
function pageUnderneath(page, paint) {
  const copy = page.cloneNode(true)
  copy.setAttribute("aria-hidden", "true")
  ;[copy, ...copy.querySelectorAll("*")].forEach((el) => {
    [...el.attributes].forEach((attr) => { if (/^data-.*-target$/.test(attr.name)) el.removeAttribute(attr.name) })
  })
  copy.querySelector(".rn-pad-shade")?.remove()
  paint(copy)
  page.before(copy)
  return copy
}

// Old text up and out, new text up and in; `apply()` runs in between.
export async function swapText(elements, apply, { stagger = 70 } = {}) {
  const leave = [{ opacity: 1, transform: "none" }, { opacity: 0, transform: "translateY(-4px)" }]
  const enter = [{ opacity: 0, transform: "translateY(4px)" }, { opacity: 1, transform: "none" }]
  await Promise.all(elements.map((el, i) => el.animate(leave, { duration: 160, delay: i * stagger, easing: EASE_OUT, fill: "forwards" }).finished))
  apply()
  await Promise.all(elements.map((el, i) => el.animate(enter, { duration: 220, delay: i * stagger, easing: EASE_OUT, fill: "forwards" }).finished))
}
